import React, {useState} from 'react'
import _ from 'lodash'
import classNames from 'classnames'
import dayjs from 'dayjs'
import {Formik} from 'formik'
import {Button, Input, message, Space} from 'antd'
import {LoadingOutlined} from '@ant-design/icons'
import {IoLockClosedOutline, IoLockOpenOutline} from 'react-icons/io5'
import {RiSearch2Line} from 'react-icons/ri'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import DrawDialog from 'components/drawer/dialog/DrawDialog'
import UserLookupComponent from 'components/UserLookupComponent'
import LookupField from 'components/LookupField'
import {Field, FormActions, Section} from 'components/form/ModernForm'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {confirmToggleSacramentsLock} from './sacramentsLockHelpers'
import ZoneLookupField from './ZoneLookupField'
import {InfoBanner} from '../../AppLocals/FamilyBook/components/MomentRow'
import FamilyBookDetailTabBar from '../../AppLocals/FamilyBook/components/FamilyBookDetailTabBar'
import FamilyBookPastoralNotesTab from '../../AppLocals/FamilyBook/components/FamilyBookPastoralNotesTab'
import {
    ALL_SACRAMENT_TYPES,
    SACRAMENT_LABELS,
    SacramentSlot
} from '../../AppLocals/FamilyBook/components/MemberDetailModal'
import {
    renderParishLookupOption,
    renderSaintLookupOption
} from '../../AppLocals/FamilyBook/functions/lookupOptionRenderers'
import {
    familyBook_changeFamilyBookParishLinkStatus_Api,
    familyBook_getFamilyBookForParishReview_Api,
    familyBook_updateFamilyBookInfoAsParish_Api,
    familyBookMember_updateFamilyBookMemberSacramentsAsParish_Api,
} from 'apis/familyBook'

// ─── Trang "Xem sổ" của Workspace (tab "Liên kết đến giáo xứ") - THAY hẳn việc điều hướng qua
// FamilyBookDetail.js (trang cá nhân, luồng gia phả phân quyền chặt chẽ - Thêm Thân Nhân/Liên kết
// sổ/Cây gia phả...) bằng 1 DrawDialog RIÊNG, PHẠM VI HẸP tự thiết kế cho đúng nhu cầu giáo xứ:
//   - Xem: thông tin sổ + từng thành viên kèm bí tích của người đó. KHÔNG hiện sổ liên quan/lịch sử
//     chuyển sổ/liên kết (đúng yêu cầu "không thấy các sổ liên quan khác").
//   - Sửa: CHỈ thông tin sổ (tên/mô tả/ngày lập/mã sổ/liên hệ) + bí tích từng thành viên. KHÔNG sửa
//     được tên/ngày sinh/giới tính/quan hệ của member (đó là gia phả của giáo dân), KHÔNG đổi được
//     giáo xứ trên sổ, KHÔNG thêm/xoá thành viên hay thao tác liên kết sổ khác.
// BE: FamilyBookController::getFamilyBookForParishReview()/updateFamilyBookInfoAsParish(),
// FamilyBookMemberController::updateFamilyBookMemberSacramentsAsParish() - route/quyền RIÊNG
// (resolveParishLinkedBook, KHÔNG dùng canEditFamilyBook), không đi qua transformer đầy đủ.

const ParishBookInfoForm = ({item, owner_type, owner_id, onChanged, t}) => {
    const {handleAsyncAction: doUpdate, loading} = useAsyncAction({
        apiInfo: familyBook_updateFamilyBookInfoAsParish_Api,
        onSuccess: () => {
            message.success(t('family book updated'))
            onChanged && onChanged()
        },
        onError: () => message.error(t('error occurred')),
    })

    const initialValues = {
        title: item.title || '',
        description: item.description || '',
        eff_date: item.eff_date ? dayjs(item.eff_date).format('YYYY-MM-DD') : '',
        source_idcode: item.source_idcode || '',
        zone: item.zone_group_id ? {id: item.zone_group_id, value: item.zone_name} : null,
        contact_name: item.contact_name || '',
        contact_phone: item.contact_phone || '',
        contact_phone2: item.contact_phone2 || '',
        contact_email: item.contact_email || '',
        contact_address: item.contact_address || '',
    }

    return (
        <Formik
            initialValues={initialValues}
            validate={(values) => (!values.title ? {title: t('required field')} : {})}
            validateOnMount
            onSubmit={({zone, ...values}) => doUpdate({
                ...values,
                zone_group_id: zone?.id || undefined,
                zone_name: zone?.value || undefined,
            }, {type: owner_type, id: owner_id, family_book_id: item.id})}>
            {({values, touched, errors, setFieldValue, handleSubmit, isSubmitting, isValid}) => (
                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                    <Section title={t('family book title')} theme="highlight">
                        <Field required error={touched.title && errors.title}>
                            <Input size="large" value={values.title} onChange={(e) => setFieldValue('title', e.target.value)}/>
                        </Field>
                    </Section>
                    <Section title={t('description')} subtitle={t('optional')}>
                        <Input.TextArea rows={2} value={values.description} onChange={(e) => setFieldValue('description', e.target.value)}/>
                    </Section>
                    <Section title={t('zone')} subtitle={t('optional')}>
                        <ZoneLookupField
                            organizationId={owner_id}
                            value={values.zone}
                            onChange={(v) => setFieldValue('zone', v)}
                            t={t}
                        />
                    </Section>
                    <Section title={t('family book info')} subtitle={t('optional')}>
                        <Field label={t('family book eff date')}>
                            <Input type="date" size="large" className="w-full" value={values.eff_date} onChange={(e) => setFieldValue('eff_date', e.target.value)}/>
                        </Field>
                        <Field label={t('family book source code')}>
                            <Input size="large" value={values.source_idcode} onChange={(e) => setFieldValue('source_idcode', e.target.value)}/>
                        </Field>
                    </Section>
                    <Section title={t('contact info')} subtitle={t('optional')}>
                        <Field label={t('contact name')}>
                            <Input size="large" value={values.contact_name} onChange={(e) => setFieldValue('contact_name', e.target.value)}/>
                        </Field>
                        <Field label={t('contact phone')}>
                            <Input size="large" value={values.contact_phone} onChange={(e) => setFieldValue('contact_phone', e.target.value)}/>
                        </Field>
                        <Field label={t('contact email')}>
                            <Input size="large" value={values.contact_email} onChange={(e) => setFieldValue('contact_email', e.target.value)}/>
                        </Field>
                        <Field label={t('address')}>
                            <Input size="large" value={values.contact_address} onChange={(e) => setFieldValue('contact_address', e.target.value)}/>
                        </Field>
                    </Section>
                    <FormActions submitText="save" isSubmitting={isSubmitting || loading} isValid={isValid} onCancel={null}/>
                </form>
            )}
        </Formik>
    )
}

// Sửa nhanh 1 bí tích của 1 thành viên - CÙNG bộ field với SacramentEditForm (MemberDetailModal.js)
// nhưng gọi API RIÊNG (updateFamilyBookMemberSacramentsAsParish, quyền theo resolveParishLinkedBook
// - KHÔNG dùng lại nguyên SacramentEditForm vì component đó gắn chặt với
// familyBook_updateFamilyBookMember_Api + canEditFamilyBook, sai quyền cho ngữ cảnh này).
const ParishSacramentEditForm = ({member, type, entry, owner_type, owner_id, onClose, onSuccess, t}) => {
    const {handleAsyncAction: doUpdate, loading} = useAsyncAction({
        apiInfo: familyBookMember_updateFamilyBookMemberSacramentsAsParish_Api,
        onSuccess: () => {
            message.success(t('sacrament updated'))
            onSuccess && onSuccess()
            onClose && onClose()
        },
        onError: () => message.error(t('error occurred')),
    })

    const initialValues = {
        eff_date: entry?.eff_date ? dayjs(entry.eff_date).format('YYYY-MM-DD') : '',
        source_idcode: entry?.source_idcode || '',
        parish: entry?.parish ? {id: entry.parish.id, value: entry.parish.name || entry.parish_name} : null,
        parish_name: entry?.parish_name || '',
        officiant_name: entry?.officiant_user?.name || entry?.officiant_name || '',
        officiant_user_id: entry?.officiant_user?.id || null,
        participant_name: entry?.participant_user?.name || entry?.participant_name || '',
        participant_user_id: entry?.participant_user?.id || null,
        participant_patron_saint: entry?.participant_patron_saint
            ? {id: entry.participant_patron_saint.id, value: entry.participant_patron_saint.name || entry?.participant_patron_saint_name}
            : null,
        participant_patron_saint_name: entry?.participant_patron_saint_name || '',
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values) => doUpdate({
                [`${type}_eff_date`]: values.eff_date || undefined,
                [`${type}_source_idcode`]: values.source_idcode || undefined,
                [`${type}_parish_id`]: values.parish?.id || undefined,
                [`${type}_parish_name`]: values.parish?.value || values.parish_name || undefined,
                [`${type}_officiant_user_id`]: values.officiant_user_id || undefined,
                [`${type}_officiant_name`]: values.officiant_name || undefined,
                [`${type}_participant_user_id`]: values.participant_user_id || undefined,
                [`${type}_participant_name`]: values.participant_name || undefined,
                [`${type}_participant_patron_saint_id`]: values.participant_patron_saint?.id || undefined,
                [`${type}_participant_patron_saint_name`]: values.participant_patron_saint?.value || values.participant_patron_saint_name || undefined,
            }, {type: owner_type, id: owner_id, family_book_member_id: member.id})}>
            {({values, setFieldValue, setValues, handleSubmit, isSubmitting}) => (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="font-bold text-lg text-color-000 uppercase tracking-wide text-center">
                        {member.name} · {t(SACRAMENT_LABELS[type])}
                    </div>

                    <Section title={t('sacrament date')} subtitle={t('optional')}>
                        <Input type="date" size="large" className="w-full" value={values.eff_date} onChange={(e) => setFieldValue('eff_date', e.target.value)}/>
                    </Section>

                    <Section title={t('parish')} subtitle={t('optional')}>
                        <LookupField
                            title={t('choose parish')}
                            createIfNotExists={true}
                            defaultValue={values.parish_name}
                            selectedOption={values.parish}
                            apiPath="/lookup/parish/get-by-types"
                            name="sacrament_parish"
                            renderOption={renderParishLookupOption}
                            handleChange={(e) => setValues({...values, parish: e.target, parish_name: e.target.value})}
                        />
                    </Section>

                    <Section title={t('officiant')} subtitle={t('optional')}>
                        <Space.Compact style={{display: 'flex', width: '100%'}}>
                            <Input
                                size="large"
                                value={values.officiant_name}
                                onChange={(e) => setValues({...values, officiant_name: e.target.value, officiant_user_id: null})}
                                allowClear
                                placeholder={t('officiant placeholder')}
                                className="flex-1"
                            />
                            <UserLookupComponent
                                handleApply={(userInfo) => {
                                    if (userInfo?.id) setValues({...values, officiant_name: userInfo.name, officiant_user_id: userInfo.id})
                                }}>
                                <Button size="large" type="primary" icon={<RiSearch2Line size={14}/>} className="flex flex-center"/>
                            </UserLookupComponent>
                        </Space.Compact>
                    </Section>

                    <Section
                        title={t(type === 'matrimony' ? 'participant_witness' : 'participant')}
                        subtitle={t(type === 'matrimony' ? 'participant_witness hint' : 'participant hint')}>
                        <div className="flex flex-col gap-2">
                            <Space.Compact style={{display: 'flex', width: '100%'}}>
                                <Input
                                    size="large"
                                    value={values.participant_name}
                                    onChange={(e) => setValues({...values, participant_name: e.target.value, participant_user_id: null})}
                                    allowClear
                                    placeholder={t(type === 'matrimony' ? 'participant_witness placeholder' : 'participant placeholder')}
                                    className="flex-1"
                                />
                                <UserLookupComponent
                                    handleApply={(userInfo) => {
                                        if (userInfo?.id) setValues({...values, participant_name: userInfo.name, participant_user_id: userInfo.id})
                                    }}>
                                    <Button size="large" type="primary" icon={<RiSearch2Line size={14}/>} className="flex flex-center"/>
                                </UserLookupComponent>
                            </Space.Compact>
                            {!values.participant_user_id && (
                                <LookupField
                                    title={t('choose patron saint')}
                                    createIfNotExists={true}
                                    defaultValue={values.participant_patron_saint_name}
                                    selectedOption={values.participant_patron_saint}
                                    apiPath="/lookup/saint/get-by-types"
                                    name="participant_patron_saint"
                                    placeholder={t('participant patron saint placeholder')}
                                    renderOption={renderSaintLookupOption}
                                    handleChange={(e) => setValues({...values, participant_patron_saint: e.target, participant_patron_saint_name: e.target.value})}
                                />
                            )}
                        </div>
                    </Section>

                    <Section title={t('family book source code')} subtitle={t('optional')}>
                        <Input size="large" value={values.source_idcode} onChange={(e) => setFieldValue('source_idcode', e.target.value)}/>
                    </Section>

                    <FormActions onCancel={onClose} submitText="save" isSubmitting={isSubmitting || loading} isValid={true}/>
                </form>
            )}
        </Formik>
    )
}

// 1 dòng thành viên - CHỈ xem tên/ngày sinh/giới tính (không sửa được, xem comment đầu file), kèm
// grid 6 ô bí tích (SacramentSlot tái dùng NGUYÊN từ MemberDetailModal.js) - mỗi ô mở
// ParishSacramentEditForm qua DrawDialog lồng bên trong (đúng pattern nested DrawDialog đã dùng ở
// MemberDetailModal.js cho chính grid này).
const ParishReviewMemberRow = ({member, owner_type, owner_id, onChanged, t}) => (
    <div className="p-3 flex flex-col gap-2 background border border-color-50 rounded-xl">
        <div className="flex items-center gap-2">
            <div style={{width: 32, height: 32}} className="flex flex-center rounded-full bg-family-100 text-family-700 font-bold flex-shrink-0 text-xs">
                {(member.name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
                <span className="font-semibold text-sm text-color-000 truncate block">{member.name}</span>
                <span className="text-xs text-color-400">
                    {member.birth_date ? dayjs(member.birth_date).format('DD/MM/YYYY') : t('not provided yet')}
                </span>
            </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
            {ALL_SACRAMENT_TYPES.map((type) => {
                const entry = (member.sacraments || []).find((s) => s.sacrament_type === type)
                return (
                    <DrawDialog
                        key={type}
                        width={420}
                        wrapClassName="family-theme"
                        component={<SacramentSlot type={type} entry={entry} t={t}/>}
                        footer={false}>
                        {(isToggle, toggle) => isToggle && (
                            <ParishSacramentEditForm
                                member={member}
                                type={type}
                                entry={entry}
                                owner_type={owner_type}
                                owner_id={owner_id}
                                onClose={() => toggle(false)}
                                onSuccess={onChanged}
                                t={t}
                            />
                        )}
                    </DrawDialog>
                )
            })}
        </div>
    </div>
)

const ParishBookReviewContent = ({bookId, owner_type, owner_id, onChangedList, t}) => {
    const [refreshToken, setRefreshToken] = useState(0)
    // Tách tab (Thông tin/Thành viên/Ghi chú mục vụ) thay vì cuộn dài 1 khối - drawer này LUÔN ở
    // góc độ quản lý của giáo xứ (owner_type/owner_id truyền thẳng, không cần điều kiện is_org_managed
    // như FamilyBookDetail.js) nên tab "Ghi chú mục vụ" hiện KHÔNG ĐIỀU KIỆN.
    const [activeTab, setActiveTab] = useState('info')
    const {response, isLoading} = useAsync({
        apiInfo: familyBook_getFamilyBookForParishReview_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}, {family_book_id: bookId}]),
        deps: [bookId, refreshToken],
    })
    const item = _.get(response, 'data')

    const {handleAsyncAction: doToggleLock, loading: togglingLock} = useAsyncAction({
        apiInfo: familyBook_changeFamilyBookParishLinkStatus_Api,
        onSuccess: () => {
            message.success(t('family book updated'))
            setRefreshToken((c) => c + 1)
            onChangedList && onChangedList()
        },
        onError: () => message.error(t('error occurred')),
    })

    const onChanged = () => {
        setRefreshToken((c) => c + 1)
        onChangedList && onChangedList()
    }

    if (isLoading && !item) {
        return <div className="flex items-center justify-center py-12"><LoadingOutlined className="text-family-500" style={{fontSize: 24}}/></div>
    }
    if (!item) return null

    return (
        <div className="flex flex-col gap-3 p-3">
            <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <span className="font-bold text-lg text-color-000 truncate block">{item.title}</span>
                    <span className="text-xs text-color-400 truncate">{item.parish_name}</span>
                </div>
                <button
                    type="button"
                    disabled={togglingLock}
                    onClick={() => confirmToggleSacramentsLock(
                        item,
                        (id, locked) => doToggleLock({sacraments_locked: locked}, {type: owner_type, id: owner_id, family_book_id: id}),
                        t
                    )}
                    title={t(item.sacraments_locked ? 'unlock sacraments hint' : 'lock sacraments hint')}
                    className={classNames(
                        'p-2 flex flex-center rounded-lg flex-shrink-0 cursor-pointer',
                        item.sacraments_locked ? 'bg-family-50 text-family-700' : 'bg-color-50 text-color-400'
                    )}>
                    {item.sacraments_locked ? <IoLockClosedOutline size={16}/> : <IoLockOpenOutline size={16}/>}
                </button>
            </div>

            <FamilyBookDetailTabBar
                tabs={[
                    {key: 'info', label: t('information')},
                    {key: 'members', label: `${t('members')} (${(item.members || []).length})`},
                    {key: 'notes', label: t('pastoral notes')},
                ]}
                activeKey={activeTab}
                onChange={setActiveTab}
            />

            {activeTab === 'info' && (
                <>
                    <InfoBanner>{t('parish review scope note')}</InfoBanner>
                    <ParishBookInfoForm item={item} owner_type={owner_type} owner_id={owner_id} onChanged={onChanged} t={t}/>
                </>
            )}

            {activeTab === 'members' && (
                <div className="flex flex-col gap-2">
                    {(item.members || []).map((member) => (
                        <ParishReviewMemberRow key={member.id} member={member} owner_type={owner_type} owner_id={owner_id} onChanged={onChanged} t={t}/>
                    ))}
                </div>
            )}

            {activeTab === 'notes' && (
                <FamilyBookPastoralNotesTab
                    ownerType={owner_type}
                    ownerId={owner_id}
                    familyBookId={item.id}
                    pastoralTag={item.pastoral_tag}
                    t={t}
                />
            )}

            <div className="h-4"/>
        </div>
    )
}

// component (trigger) - do CALLER (FamilyBookCard) truyền vào, thay Link điều hướng cũ. onChanged -
// gọi khi có sửa (thông tin sổ/bí tích/khoá) để list cha (ParishSection) refetch đúng dữ liệu mới.
const ParishBookReviewDrawer = ({bookId, owner_type, owner_id, trigger, onChanged, t}) => (
    <DrawDialog width={480} wrapClassName="family-theme" component={trigger} footer={false}>
        {(isToggle) => isToggle && (
            <ParishBookReviewContent bookId={bookId} owner_type={owner_type} owner_id={owner_id} onChangedList={onChanged} t={t}/>
        )}
    </DrawDialog>
)

export default ParishBookReviewDrawer
